/**
 * Archive Service
 *
 * Client for the archive-entry preview endpoint that landed with Task 19.
 *
 * Backend contract:
 *   GET /api/v2/content/:contentId/archive/entry?path=<encoded-path>&team_id=<id>
 *   Returns raw entry bytes with Content-Type/Content-Length/Content-Disposition.
 *   Errors are JSON of shape { error: { code, message, requestId } }.
 *
 * Caller MUST revoke any returned object URL (URL.revokeObjectURL) when no
 * longer needed to avoid leaking memory — typically in a useEffect cleanup
 * or when selecting a different entry.
 */

import { httpClientService } from './core';

/** Hard cap enforced by the backend (50 MB). Surfaced here so the UI can
 *  pre-emptively avoid wasting a request on an entry it already knows is too big. */
export const MAX_ARCHIVE_PREVIEW_ENTRY_SIZE = 50 * 1024 * 1024;

export type ArchiveEntryErrorCode =
  | 'ARCHIVE_ENTRY_NOT_FOUND'
  | 'ARCHIVE_ENTRY_TOO_LARGE'
  | 'ARCHIVE_PREVIEW_UNSUPPORTED_FORMAT'
  | 'NOT_AN_ARCHIVE'
  | 'ARCHIVE_EXTRACTION_FAILED'
  | 'CONTENT_NOT_FOUND'
  | 'NETWORK_ERROR';

export class ArchiveEntryError extends Error {
  constructor(
    public readonly code: ArchiveEntryErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ArchiveEntryError';
  }
}

export interface ArchiveEntryBlob {
  blob: Blob;
  url: string;            // object URL — caller is responsible for revoking when done
  mimeType: string;
  fileName: string;       // basename from Content-Disposition or fallback
}

/**
 * Fetch a single entry from an uploaded archive for inline preview.
 *
 * The error body for failures is JSON. When axios is configured with
 * responseType: 'blob' an error response body arrives as a Blob, so we
 * read it back to text and parse it to recover the error code.
 */
/**
 * Fetch a single entry from an archive content item accessed via a public link.
 * Calls GET /api/v2/public/:linkId/archive/entry — no authentication required.
 */
export async function fetchArchiveEntryForPublicLink(
  linkId: string,
  token: string,
  entryPath: string,
  password?: string,
  shareToken?: string,
  contentAccessToken?: string,
): Promise<ArchiveEntryBlob> {
  const params = new URLSearchParams();
  params.set('token', token);
  params.set('path', entryPath);
  if (password) params.set('password', password);
  if (shareToken) params.set('shareToken', shareToken);
  if (contentAccessToken) params.set('accessToken', contentAccessToken);
  try {
    const response = await httpClientService.get<Blob>(
      `/public/${linkId}/archive/entry?${params.toString()}`,
      { responseType: 'blob' },
    );
    const blob: Blob = response.data;
    const url = URL.createObjectURL(blob);
    const contentDisposition = (response.headers as any)?.['content-disposition'] || '';
    const fileNameMatch = /filename="([^"]+)"/i.exec(contentDisposition);
    const fileName = fileNameMatch?.[1] || entryPath.split('/').pop() || 'entry';
    return { blob, url, mimeType: blob.type || 'application/octet-stream', fileName };
  } catch (err: any) {
    let code: ArchiveEntryErrorCode = 'NETWORK_ERROR';
    let message = err?.message || 'Failed to fetch archive entry';
    const errData = err?.response?.data;
    if (errData instanceof Blob) {
      try {
        const text = await errData.text();
        const parsed = JSON.parse(text);
        if (parsed?.error?.code) code = parsed.error.code as ArchiveEntryErrorCode;
        if (parsed?.error?.message) message = parsed.error.message;
      } catch { /* keep defaults */ }
    } else if (errData?.error?.code) {
      code = errData.error.code as ArchiveEntryErrorCode;
      if (errData.error.message) message = errData.error.message;
    }
    throw new ArchiveEntryError(code, message);
  }
}

export async function fetchArchiveEntry(
  contentId: string,
  entryPath: string,
  teamId: string,
): Promise<ArchiveEntryBlob> {
  try {
    const params = new URLSearchParams();
    params.set('path', entryPath);
    params.set('team_id', teamId);
    const response = await httpClientService.get<Blob>(
      `/content/${contentId}/archive/entry?${params.toString()}`,
      { responseType: 'blob' },
    );
    const blob: Blob = response.data;
    const url = URL.createObjectURL(blob);
    const contentDisposition = (response.headers as any)?.['content-disposition'] || '';
    const fileNameMatch = /filename="([^"]+)"/i.exec(contentDisposition);
    const fileName = fileNameMatch?.[1] || entryPath.split('/').pop() || 'entry';
    return {
      blob,
      url,
      mimeType: blob.type || 'application/octet-stream',
      fileName,
    };
  } catch (err: any) {
    let code: ArchiveEntryErrorCode = 'NETWORK_ERROR';
    let message = err?.message || 'Failed to fetch archive entry';

    // When responseType is blob, an error response's `data` is itself a Blob
    // containing JSON. We need to read it back as text to extract the code.
    const errData = err?.response?.data;
    if (errData instanceof Blob) {
      try {
        const text = await errData.text();
        const parsed = JSON.parse(text);
        if (parsed?.error?.code) code = parsed.error.code as ArchiveEntryErrorCode;
        if (parsed?.error?.message) message = parsed.error.message;
      } catch {
        // Couldn't parse — keep NETWORK_ERROR / generic message.
      }
    } else if (errData?.error?.code) {
      code = errData.error.code as ArchiveEntryErrorCode;
      if (errData.error.message) message = errData.error.message;
    }
    throw new ArchiveEntryError(code, message);
  }
}
